import { DatePipe, CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  input
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import {
  ActionSheetController,
  AlertController,
  IonModal,
  IonicModule,
  ModalController,
  NavController,
  isPlatform,
} from "@ionic/angular";
import { Store } from "@ngrx/store";
import { selectLessonById, selectPasstLessonsForStudent, selectSelectedLesson } from "src/app/calendar/ngrx/calendar.selectors";
import { InvoiceDto } from "src/app/office/interfaces/invoice_dto";
import { LessonInvoiceAddRequest } from "src/app/office/interfaces/lesson_invoice_add_request";
import { DialogService } from '../../../shared/services/dialog.service';
import { selectInvoice } from "src/app/office/invoice-ngrx/invoices.actions";
import { selectInvoiceById } from "src/app/office/invoice-ngrx/invoices.selectors";
import { InvoiceItemComponent } from "src/app/office/components/invoice-item/invoice-item.component";
import { selectStudentById } from "src/app/students/ngrx/students.selectors";
import { OrderPipe } from "src/app/shared/pipes/order.pipe";
import { LessonAccordionComponent } from "src/app/students/components/lesson-accordion/lesson-accordion.component";
import { InvoiceItemDto } from "src/app/office/interfaces/invoice_item_dto";
import { selectEdcuationCategoryByCategory } from "src/app/settings/ngrx/settings.selectors";
import { loadLessonRatingsForLesson, loadLessonRatingsForStudent, selectSetting } from "src/app/settings/ngrx/settings.action";
import { getTrackingsByAppointmentId } from "src/app/tracking/ngrx/tracking.actions";
import { ModeService } from "src/app/utils/mode.service";
import { deleteAppointment } from "src/app/calendar/ngrx/calendar.actions";
import { selectCustomer } from "src/app/auth/ngrx/customer.selectors";
import { calculateTotalTime } from "src/app/utils/DateUtils";

@Component({
    selector: "app-lesson-detail",
    templateUrl: "./lesson-detail.component.html",
    styleUrls: ["./lesson-detail.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        FormsModule,
        RouterModule,
        ReactiveFormsModule,
        DatePipe,
        InvoiceItemComponent,
        OrderPipe,
        LessonAccordionComponent,
        CommonModule,
    ]
})
export class LessonDetailComponent {
  @ViewChild("lessonModal") lessonModal: IonModal;
  @ViewChild("invoiceModal") invoiceModal: IonModal;
  readonly lessonId = input<number>(undefined);
  readonly mobile = input(true);
  readonly showCloseButton = input(false);
  @Output() invoiceClick: EventEmitter<any> = new EventEmitter();
  @Output() unselect: EventEmitter<any> = new EventEmitter();
  calculateTotalTime = calculateTotalTime;
  originPage = "";
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  public modalController = inject(ModalController);
  private dialogService = inject(DialogService);
  public modeService = inject(ModeService);
  private alertController = inject(AlertController);
  private actionSheetCtrl = inject(ActionSheetController);
  private navController = inject(NavController);

  invoiceSignal = computed(() => this.store.selectSignal(selectInvoiceById(this.lesson()?.InvoiceId))());
  studentSignal = computed(() => this.store.selectSignal(selectStudentById(this.lesson()?.StudentId))());
  customerSignal = this.store.selectSignal(selectCustomer);
  lesson = computed(() => {
    const lessonId = this.lessonId();
    return lessonId ? this.store.selectSignal(selectLessonById(lessonId))() : this.store.selectSignal(selectSelectedLesson)();
  });
  oldLessons = computed(() => this.store.selectSignal(selectPasstLessonsForStudent(this.lesson()?.StudentId, this.lesson()?.Id))());
  newInvoice = signal<LessonInvoiceAddRequest | null>(null);
  today = new Date();
  androidFakeLoading = signal<boolean>(isPlatform("android") ? true : false);$
  loadedId: number;
  viewInit = signal(false);

  constructor() {
    effect(() => this.loadData(), { allowSignalWrites: true });
    this.originPage = isPlatform("ios")
    ? this.route.snapshot.paramMap.get("originPage")
    : "";
  }

  ionViewDidEnter() {
    this.androidFakeLoading.set(false);
    this.viewInit.set(true);
  }

  ngOnDestroy() {}

  loadData = () => {
    if (this.androidFakeLoading()) {
      return;
    }

    if (!this.lesson()) {
      this.unselect.emit();
    }

    if (this.loadedId !== this.lesson()?.Id) {
      this.store.dispatch(loadLessonRatingsForLesson({ lessonId: this.lesson()?.Id }));
      this.store.dispatch(loadLessonRatingsForStudent({ studentId: this.lesson()?.StudentId }));
      this.store.dispatch(getTrackingsByAppointmentId({ appointmentId: this.lesson()?.Id }));
      this.loadedId = this.lesson()?.Id;
    }
  };

  openInvoice = (invoice: InvoiceDto) => {
    this.store.dispatch(selectInvoice({ invoice: invoice }));
    this.dialogService.openInvoiceDialog(invoice);
  };

  openMaps = () => {
    const lessonDuration = (new Date(this.lesson()?.EndTime).getTime() - new Date(this.lesson()?.StartTime).getTime()) / 60000;
    this.dialogService.openMaps(this.lesson()?.Id, lessonDuration);
  };

  openNewInvoiceDialog = async (lesson = this.lesson()) => {
    if (
      this.customerSignal().iban &&
      this.customerSignal().brand &&
      this.customerSignal().city &&
      this.customerSignal().mail_customer &&
      this.customerSignal().postal_code &&
      this.customerSignal().street
    ) {
      const student_id = this.studentSignal();
      this.newInvoice.set({
        student_id: student_id,
        invoice_items: [{
          id: lesson?.Id,
          appointment_id: lesson?.Id,
          prize: lesson?.Prize,
          name: lesson?.Type || lesson?.Subject,
          date: lesson?.StartTime.toISOString().slice(0, 10),
          amount: lesson?.Lessons,
          unit: lesson?.Lessons > 1 ? 'Lektionen' : 'Lektion',
          subscription_payment: lesson?.SubscriptionPayment,
          initial_prize: lesson?.Prize,
        } as InvoiceItemDto]
      });
      this.dialogService.openNewInvoiceDialog(this.newInvoice());
    } else {
      this.store.dispatch(selectSetting({ setting: "company" }));
      await this.dialogService.openSettings("company", "Um diese Funktion nutzen zu können, benötigen wir weitere Informationen. Möchtest du sie in den Einstellungen hinterlegen?");
    }
  };

  openModal = () => {
    const updatedLesson = { ...this.lesson(), Student: this.studentSignal() };
    this.dialogService.openNewLessonDialog(updatedLesson);
  };

  openMileageDialog = () => {
    this.dialogService.openMileageDialog(this.lesson());
  };

  presentDeleteActionSheet = async () => {
    const actionSheet = await this.actionSheetCtrl.create({
      header: "Aktionen",
      buttons: this.deleteActionSheetButtons,
    });

    await actionSheet.present();
  };

  deleteActionSheetButtons = [
    {
      text: "Löschen",
      role: "destructive",
      handler: () => {
        this.presentDeleteAlert();
      },
    },
    {
      text: "Abbrechen",
      role: "cancel",
      data: {
        action: "cancel",
      },
    },
  ];

  presentDeleteAlert = async () => {
    const alert = await this.alertController.create({
      header: "Achtung!",
      message: "Bist du sicher, dass du diesen Termin löschen möchtest?",
      buttons: this.deleteAlertButtons,
    });

    await alert.present();
  };

  deleteAlertButtons = [
    {
      text: "Abbrechen",
      role: "cancel",
      handler: () => {
        this.alertController.dismiss();
      },
    },
    {
      text: "OK",
      role: "confirm",
      handler: () => {
        this.deleteLesson();
        this.alertController.dismiss();
      },
    },
  ];

  deleteLesson = () => {
    const lesson = this.lesson();
    if (lesson) {
      if (this.customerSignal().google_calendar_id) {
        this.store.dispatch(deleteAppointment({ appointmentId: lesson.Id, googleCalendarId: this.customerSignal().google_calendar_id, googleId: lesson.GoogleId })); 
      } if (this.customerSignal().local_calendar_connected) {
        this.store.dispatch(deleteAppointment({ appointmentId: lesson.Id, localCalendarId: lesson.LocalCalendarId }));
      }
      else {
        this.store.dispatch(deleteAppointment({ appointmentId: lesson.Id }));
      }
      this.navController.pop();
      this.modalController.dismiss();
    }
  };

  getGoogleMapsLink(location: string) {
    const link = isPlatform("ios") ? "maps://www.google.com/?q=" + location : "https://www.google.com/maps/search/?api=1&query=" + location;
    return link;
  }

  openEducationCard = () => {
    const educationCategory = this.store.selectSignal(selectEdcuationCategoryByCategory(this.studentSignal()?.category))();
    this.dialogService.openEducationCard(educationCategory, this.studentSignal(), this.lesson(), true);
  };

  openExamDialog = () => {
    this.dialogService.openExamDialog(this.lesson(), this.studentSignal());
  };

  contactStudent = () => {
    const mobile_no = this.studentSignal()?.mobile_no.replace(/\s/g, "");
    window.open(`https://api.whatsapp.com/send?phone=${mobile_no}&text=Hallo%20${this.studentSignal()?.forename}%2C%0A%0A`, "_blank");
  };

  cancel() {
    this.modalController.dismiss();
  }
}